import { UseFormReturn } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { FormStepHeader } from './components/form-step-header';
import { BasicInfoForm } from './schema';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import useUpdateMatter from '@/api/mutations/useUpdateMatter';
import { Icon } from '@/new/components/icon';
import { PageContainer } from '@/new/components/page-container';
import { PageHeader } from '@/new/components/page-header';
import { Button } from '@/new/components/shadcn/button';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/new/components/shadcn/form';
import { Input } from '@/new/components/shadcn/input';

interface BasicInfoStepProps {
  form: UseFormReturn<BasicInfoForm>;
}

export function MatterCreatorBasic({ form }: BasicInfoStepProps) {
  const { matterId } = useParams();
  const navigate = useNavigate();
  const { mutateAsync: updateMatter, isLoading } = useUpdateMatter(matterId ?? '');

  const handleNext = async () => {
    const isValid = await form.trigger();
    if (isValid) {
      try {
        const values = form.getValues();
        trackCustomEvent(TrackingEventsV2.UpdateMatter, { matterId });
        await updateMatter({
          name: values.name,
          code: values.code,
          tokenLimit: values.tokenLimit,
        });
        navigate(`/matters/new/${matterId}/details`);
      } catch (error) {
        toast.error('Failed to update matter');
      }
    }
  };

  return (
    <>
      <PageContainer>
        <PageHeader>
          <FormStepHeader
            title="Basic Information"
            description="Give this new matter a name and optionally set page limits."
            step={2}
          >
            <Button variant="ghost" asChild>
              <Link to={`/matters/new/${matterId}/files`}>
                <Icon className="lucide--arrow-left" />
                Back
              </Link>
            </Button>
            <Button variant="primary" onClick={handleNext} disabled={isLoading}>
              Next: Matter Details
              <Icon className="lucide--arrow-right" />
            </Button>
          </FormStepHeader>
        </PageHeader>

        <div className="flex items-start justify-between">
          <div className="mx-auto mt-12 w-full max-w-xl">
            <div className="space-y-6">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormMessage />
                    <FormLabel required>Matter Name</FormLabel>
                    <p className="-mt-0.5 mb-3 text-xs whitespace-normal text-gray-500">This can be updated later.</p>
                    <FormControl>
                      <Input {...field} placeholder="Company A vs Company B" data-testid="matter-creator-name-field" />
                    </FormControl>
                  </FormItem>
                )}
              />

              <hr className="my-10" />

              <FormField
                control={form.control}
                name="code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Matter Code <span className="font-normal text-gray-400">Optional</span>
                    </FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="WEX-123" data-testid="matter-creator-code-field" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="tokenLimit"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Matter Page Limit <span className="font-normal text-gray-400">Optional</span>
                      <p className="mt-2 text-xs whitespace-normal text-gray-500">
                        The maximum number of pages that can be uploaded to this matter before always requiring
                        approval.
                      </p>
                    </FormLabel>

                    <FormControl>
                      <Input
                        {...field}
                        type="number"
                        placeholder="e.g. 50,000"
                        data-testid="matter-creator-token-limit-field"
                        min="0"
                        step="1000"
                        onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                        value={field.value ?? ''}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
        </div>
      </PageContainer>
    </>
  );
}
